import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BrandM,
  BrandS,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { TermsDialog } from '@sorare/core/src/components/conversionCredit/TermsDialog';
import { MonetaryRewardDisclaimer } from '@sorare/core/src/components/jonum/MonetaryRewardDisclaimer';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { useQuickplayAssets } from '@sorare/core/src/hooks/useQuickplayAssets';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { TutorialBezel } from '#src/components/quickPlay/QuickPlayTutorialDialog/TutorialBezel/index.tsx';
import { ESTIMATED_CASH_PRIZE_IN_USER_CURRENCY } from '#src/components/quickPlay/QuickPlayTutorialDialog/utils.tsx';

const StyledNeonEdge = styled(NeonEdge)`
  position: absolute;
  inset: 0;
`;
const StyledBezel = styled(Bezel)`
  padding: var(--double-unit);
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  justify-self: center;
`;
const TextWrapper = styled(Horizontal)`
  align-items: baseline;
`;

const StyledButtonBase = styled(ButtonBase)`
  text-decoration: underline;
  color: var(--c-nd-600);
  width: 100%;
  text-align: center;

  @media ${tabletAndAbove} {
    position: absolute;
    bottom: calc(-1 * var(--quadruple-unit));
  }
`;

export const GetRewarded = () => {
  const assets = useQuickplayAssets();
  const [openTermsDialog, setOpenTermsDialog] = useState(false);
  return (
    <Vertical style={{ position: 'relative' }}>
      <TutorialBezel color="#757AEB">
        <Vertical gap={3} center>
          <Vertical center className="text-center">
            <BrandM>
              <FormattedMessage defaultMessage="Get rewarded" />
            </BrandM>
            <BodyM color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Claim your reward right after the game" />
            </BodyM>
          </Vertical>
          <StyledBezel background="var(--c-black)" borderColor="#757aeb">
            <Horizontal gap={2}>
              <RewardConfigImage
                anyRewardConfig={{
                  __typename: 'MonetaryRewardConfig',
                  id: '1',
                }}
                size={5}
              />
              <TextWrapper gap={0.5}>
                <BrandM>
                  <MonetaryRewardDisclaimer variant="monetary">
                    <FormattedNumber
                      value={
                        assets?.totalCashPrizePool ||
                        ESTIMATED_CASH_PRIZE_IN_USER_CURRENCY
                      }
                      style="currency"
                      currency="USD"
                    />
                  </MonetaryRewardDisclaimer>
                </BrandM>
                <BrandS color="var(--c-nd-600)">
                  <FormattedMessage defaultMessage="Top Reward" />
                </BrandS>
              </TextWrapper>
            </Horizontal>
            <StyledNeonEdge showHalo="never" color="#c9ccfe" />
          </StyledBezel>
        </Vertical>
        {!!openTermsDialog && (
          <TermsDialog
            onClose={() => setOpenTermsDialog(false)}
            terms="quickPlayTerms"
          />
        )}
      </TutorialBezel>
      <StyledButtonBase onClick={() => setOpenTermsDialog(true)}>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="Quickplay Rules Apply" />
        </LabelM>
      </StyledButtonBase>
    </Vertical>
  );
};
